import React, { Suspense, useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import NavbarHeader from './components/commonComponents/navbar/NavBarHeader';
import '@amzn/awsui-components-react/index.min.css';
import './app.css';
import AppLayout from '@amzn/awsui-components-console/app-layout';
import ContentLayout from '@amzn/awsui-components-console/content-layout';
import Icon from '@amzn/awsui-components-console/icon';
import Flashbar from '@amzn/awsui-components-console/flashbar';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import { I18nProvider } from '@amzn/awsui-components-console/i18n';
import messages from '@amzn/awsui-components-console/i18n/messages/all.en';
import SidePanel from './components/commonComponents/sidePanel/SidePanel';
import { Helmet } from 'react-helmet';
import Spinner from '@amzn/awsui-components-console/spinner';
import { GlobalProviders, GlobalSpinner } from './providers/GlobalProviders';
import { useAuthContext } from './providers/AuthContext';
import HelpPanel from '@amzn/awsui-components-console/help-panel';
import AppHelpPanel from './components/commonComponents/helpPanel/HelpPanel';
import AnnotationContext from '@amzn/awsui-components-console/annotation-context';
import { ExternalLinkItem } from './components/commonComponents/ExternalLinkItem.jsx';
import ChatbotWidget from './components/commonComponents/ChatbotWidget';
import { ROUTE_CONFIG_BY_PATH } from 'src/config/routeConfig';
import { useGlobalAlertStore } from 'src/stores/globalAlertStore/globalAlertStore';
import { FlashbarItem } from 'src/components/WhatsNew/FeatureType';
import { deleteFlashbarItem } from 'src/services/flashbarService';
import { TUTORIAL_KEYNAME_LOCAL_STORAGE } from 'src/constants/browserStorageConstants';
import { getDefaultSidePanelOpen } from 'src/stores/uiPreferencesStore';
import AppRoutes from './config/AppRoutes';
import { TUTORIAL_CONFIG, TUTORIAL_I18N_STRINGS } from './config/tutorialConfig';
import { logger } from 'src/utils/logger';

interface ToolsLink {
	href: string;
	text: string;
}

interface ToolsConfig {
	title: React.ReactNode;
	content: React.ReactNode;
	links: ToolsLink[];
}

interface Notification {
	id: string;
	dismissible: boolean;
	[key: string]: unknown;
}

export interface CommonProps {
	setTools: (toolsConfig: ToolsConfig) => void;
	openTools: () => void;
	closeTools: () => void;
	hideTools: () => void;
	addNotification: (notification: Omit<Notification, 'id' | 'dismissible'>) => void;
}

function App() {
	const { alerts, dismissedAlertIds, dismissAlert, overrideExecution, fetchAlerts, checkOverridePermission, canUserOverride, hasOverridden } = useGlobalAlertStore();
	const location = useLocation();

	const { userName: authUserName } = useAuthContext();

	const [toolsTitle, setToolsTitle] = useState<React.ReactNode>('');
	const [toolsContent, setToolsContent] = useState<React.ReactNode>(<div></div>);
	const [toolsLink, setToolsLink] = useState<ToolsLink[]>([]);
	const [useAppHelpPanel, setUseAppHelpPanel] = useState(true);
	const [toolsOpen, setToolsOpen] = useState(false);
	const [navigationOpen, setNavigationOpen] = useState(getDefaultSidePanelOpen);
	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [isOverrideModalVisible, setIsOverrideModalVisible] = useState(false);
	const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
	const [alertToDelete, setAlertToDelete] = useState<FlashbarItem | null>(null);

	const [isTutorialCompleted, setIsTutorialCompleted] = useState(
		() => !!localStorage.getItem(TUTORIAL_KEYNAME_LOCAL_STORAGE)
	);

	useEffect(() => {
		fetchAlerts();
	}, [fetchAlerts]);

	useEffect(() => {
		if (authUserName) {
			logger.info(`Checking override permission [ userName ] ::: [ ${authUserName} ]`);
			checkOverridePermission(authUserName);
		}
	}, [authUserName, checkOverridePermission]);

	const currentPage = useMemo(() => {
		const path = location.pathname;

		for (const [route, config] of Object.entries(ROUTE_CONFIG_BY_PATH)) {
			if (path.includes(route) && config.helpContext) {
				return config.helpContext;
			}
		}

		return 'overview';
	}, [location.pathname]);

	const onTutorialFinish = useCallback(() => {
		setIsTutorialCompleted(true);
		localStorage.setItem(TUTORIAL_KEYNAME_LOCAL_STORAGE, '1');
	}, []);

	const setTools = useCallback((toolsConfig: ToolsConfig) => {
		setToolsTitle(toolsConfig.title);
		setToolsContent(toolsConfig.content);
		setToolsLink(toolsConfig.links);
		setUseAppHelpPanel(false);
	}, []);

	const openTools = useCallback(() => setToolsOpen(true), []);
	const closeTools = useCallback(() => setToolsOpen(false), []);
	const hideTools = useCallback(() => setUseAppHelpPanel(true), []);

	const addNotification = useCallback((notification: Omit<Notification, 'id' | 'dismissible'>) => {
		const id = uuidv4();

		setNotifications(prev => [...prev, { ...notification, id, dismissible: true }]);
	}, []);

	const handleDeleteGlobalAlert = useCallback(async (alertId: string) => {
		try {
			await deleteFlashbarItem(alertId);
			await useGlobalAlertStore.getState().refresh();
		} catch (error) {
			logger.error(`Failed to delete global alert [ alertId ] ::: [ ${alertId} ] [ error ] ::: [ ${JSON.stringify(error)} ]`);
		}
	}, []);

	const dismissNotification = useCallback((id: string) => {
		setNotifications(prev => prev.filter(n => n.id !== id));
	}, []);


	const commonProps: CommonProps = useMemo(() => ({
		setTools,
		openTools,
		closeTools,
		hideTools,
		addNotification,
	}), [setTools, openTools, closeTools, hideTools, addNotification]);

	const globalAlertFlashbarItems = useMemo(
		() => alerts
			.filter((alert) => !dismissedAlertIds.includes(alert.id))
			.map((alert) => {
				const isBlocking = alert.isBlockingExecution;
				const isOverridden = isBlocking && canUserOverride && hasOverridden;

				return {
					type: (isOverridden ? 'warning' : alert.type) as 'success' | 'error' | 'warning' | 'info',
					header: isOverridden
						? `${alert.header} (Overridden)`
						: alert.header,
					content: isOverridden
						? 'You have overridden this block. Executions are enabled for your session.'
						: alert.content,
					dismissible: !isBlocking,
					id: alert.id,
					onDismiss: !isBlocking ? () => dismissAlert(alert.id) : undefined,
					...(canUserOverride && {
						action: (
							<SpaceBetween direction="horizontal" size="xs">
								{isBlocking && !hasOverridden && (
									<Button variant="normal" onClick={() => setIsOverrideModalVisible(true)}>Override</Button>
								)}
								<Button variant="icon" iconName="remove" ariaLabel="Delete alert" onClick={() => {
									setAlertToDelete(alert);
									setIsDeleteModalVisible(true);
								}} />
							</SpaceBetween>
						),
					}),
				};
			}),
		[alerts, dismissedAlertIds, canUserOverride, hasOverridden, dismissAlert]
	);

	const flashbarItems = useMemo(
		() => [
			...globalAlertFlashbarItems,
			...notifications.map(notification => ({
				...notification,
				onDismiss: () => dismissNotification(notification.id),
			})),
		],
		[globalAlertFlashbarItems, notifications, dismissNotification]
	);

	return (
		<I18nProvider locale="en" messages={[messages]}>
			<GlobalProviders>
				<GlobalSpinner>
					<Helmet>
						<link rel="icon" href="/images/Logo.ico" />
					</Helmet>
					<Suspense fallback={<Spinner size="large" />}>
						<NavbarHeader />
						<AnnotationContext
							currentTutorial={{
								...TUTORIAL_CONFIG,
								completed: isTutorialCompleted,
							}}
							onFinish={onTutorialFinish}
							onStartTutorial={() => {}}
							onExitTutorial={() => {}}
							i18nStrings={TUTORIAL_I18N_STRINGS}
						>
							<AppLayout
								headerSelector="#header"
								// TODO: To be uncommented after adding visual refresh
								// breadcrumbs={<BreadcrumbGroup items={getBreadcrumbs()} />}
								navigation={<SidePanel />}
								navigationOpen={navigationOpen}
								navigationHide={false}
								onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
								onToolsChange={({ detail }) => setToolsOpen(detail.open)}
								maxContentWidth={Number.MAX_VALUE}
								notifications={
									<Flashbar items={flashbarItems} />
								}
								content={
									<ContentLayout disableOverlap>
										<AppRoutes commonProps={commonProps} location={location} />
									</ContentLayout>
								}
								tools={
									useAppHelpPanel ? (
										<AppHelpPanel currentPage={currentPage} />
									) : (
										<HelpPanel
											header={toolsTitle}
											footer={
												<>
													<h3>
														Learn more{' '}
														<span role="img" aria-label="Icon external Link">
															<Icon name="external" />
														</span>
													</h3>
													<ul>
														{toolsLink.map((link, i) => (
															<li key={i}>
																<ExternalLinkItem
																	href={link.href}
																	text={link.text}
																/>
															</li>
														))}
													</ul>
												</>
											}
										>
											{toolsContent}
										</HelpPanel>
									)
								}
								toolsHide={false}
								toolsOpen={toolsOpen}
							/>
						</AnnotationContext>

						{/* Override Confirmation Modal */}
						<Modal
							visible={isOverrideModalVisible}
							onDismiss={() => setIsOverrideModalVisible(false)}
							header="Override Execution Block"
							footer={
								<Box float="right">
									<SpaceBetween direction="horizontal" size="xs">
										<Button variant="link" onClick={() => setIsOverrideModalVisible(false)}>Cancel</Button>
										<Button variant="primary" onClick={() => {
											overrideExecution();
											setIsOverrideModalVisible(false);
										}}>Confirm Override</Button>
									</SpaceBetween>
								</Box>
							}
						>
							Are you sure you want to override this execution block? You will be able to run workflows, but other users will still be blocked.
						</Modal>

						{/* Delete Confirmation Modal */}
						<Modal
							visible={isDeleteModalVisible}
							onDismiss={() => {
								setIsDeleteModalVisible(false);
								setAlertToDelete(null);
							}}
							header="Delete Global Alert"
							footer={
								<Box float="right">
									<SpaceBetween direction="horizontal" size="xs">
										<Button variant="link" onClick={() => {
											setIsDeleteModalVisible(false);
											setAlertToDelete(null);
										}}>Cancel</Button>
										<Button variant="primary" onClick={async () => {
											if (alertToDelete) {
												await handleDeleteGlobalAlert(alertToDelete.id);
											}
											setIsDeleteModalVisible(false);
											setAlertToDelete(null);
										}}>Delete</Button>
									</SpaceBetween>
								</Box>
							}
						>
							{alertToDelete?.isBlockingExecution
								? 'Are you sure you want to permanently delete this global alert? This will remove the alert and unblock executions for all users. This action cannot be undone.'
								: 'Are you sure you want to permanently delete this global alert? This will remove the alert for all users. This action cannot be undone.'
							}
						</Modal>
						<ChatbotWidget />
					</Suspense>
				</GlobalSpinner>
			</GlobalProviders>
		</I18nProvider>
	);
}

export default App;
